// API Configuration
// Prefer relative base so it works in dev (Vite proxy) and prod (Vercel routes)
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

// Authentication header injection helper
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  
  try {
    // Add session ID if available
    const sessionId = sessionStorage.getItem('session_id')
    if (sessionId) {
      headers['X-Session-ID'] = sessionId
    }
    
    // Add user ID for server-side logging (optional)
    const userId = sessionStorage.getItem('user_id')
    if (userId) {
      headers['X-User-ID'] = userId
    }
  } catch (error) {
    // Ignore sessionStorage errors (e.g., in server-side rendering)
    console.warn('Failed to retrieve session data:', error)
  }
  
  return headers
}

// Generic API fetch wrapper with enhanced error handling
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    // Clean up endpoint to prevent double slashes and malformed URLs
    const cleanEndpoint = endpoint.replace(/^\/+/, '').replace(/\/+/g, '/')
    const url = `${API_BASE_URL}/${cleanEndpoint}`
    
    // Inject authentication headers
    const authHeaders = getAuthHeaders()
    
    console.log('📡 API Request:', { 
      url, 
      method: options.method || 'GET', 
      hasAuth: Object.keys(authHeaders).length > 0,
      sessionId: authHeaders['X-Session-ID']?.substring(0, 8) + '...' || 'none'
    })
    
    // Add timeout and connection error handling
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      credentials: 'include', // Include cookies for session management
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    })
    
    clearTimeout(timeoutId)

    let data: any
    try {
      const responseText = await response.text()
      console.log('Raw API Response:', { status: response.status, text: responseText })
      
      if (!responseText.trim()) {
        return {
          success: false,
          error: 'Empty response from server',
        }
      }
      
      data = JSON.parse(responseText)
    } catch (parseError) {
      console.error('JSON Parse Error:', parseError)
      return {
        success: false,
        error: 'Invalid server response. Please check server configuration.',
      }
    }
    
    console.log('Parsed API Response:', { status: response.status, data })

    if (!response.ok) {
      // Handle specific HTTP status codes
      let errorMessage = data.error || 'API request failed'
      
      switch (response.status) {
        case 400:
          errorMessage = data.error || 'Bad request. Please check your input.'
          break
        case 401:
          // Handle authentication errors
          errorMessage = data.error || 'Session expired. Please log in again.'
          
          // Clear session data on 401 to force re-login
          try {
            sessionStorage.removeItem('session_id')
            sessionStorage.removeItem('user_id')
            console.warn('🔄 API: Session expired, clearing local session data')
            
            // Redirect to login if on admin route
            if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
              console.log('🔄 API: Redirecting to login due to 401 error')
              window.location.href = '/admin/login'
            }
          } catch (storageError) {
            console.warn('Failed to clear session storage:', storageError)
          }
          break
        case 403:
          errorMessage = data.error || 'Access denied. You don\'t have permission for this action.'
          break
        case 404:
          errorMessage = data.error || 'Resource not found. Please check the URL or contact support.'
          break
        case 422:
          errorMessage = data.error || 'Invalid data provided. Please check your input and try again.'
          break
        case 429:
          errorMessage = data.error || 'Too many requests. Please wait a moment and try again.'
          break
        case 500:
          errorMessage = data.error || 'Server error occurred. Please try again later.'
          break
        case 502:
          errorMessage = data.error || 'Bad gateway. Server is temporarily unavailable.'
          break
        case 503:
          errorMessage = data.error || 'Service temporarily unavailable. Please try again later.'
          break
        case 504:
          errorMessage = data.error || 'Request timeout. The server took too long to respond.'
          break
        default:
          errorMessage = data.error || `HTTP ${response.status}: ${response.statusText}`
      }
      
      return {
        success: false,
        error: errorMessage,
      }
    }

    return data
  } catch (error) {
    console.error('API Error:', error)
    
    // Handle different types of errors
    let errorMessage = 'An unexpected error occurred'
    
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      errorMessage = 'Connection failed. Please check if the server is running and accessible.'
    } else if (error instanceof Error) {
      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout. Please check your connection and try again.'
      } else if (error.message.includes('NetworkError') || error.message.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.'
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Server configuration error. Please contact support.'
      } else {
        errorMessage = error.message
      }
    }
    
    return {
      success: false,
      error: errorMessage,
    }
  }
}

// Package API types (keeping for backward compatibility)
// (Auth types removed; v2 API is public-only)

export interface PackageData {
  id: number
  title: string
  location: string
  category: string
  currency: string
  price: number
  duration: string
  days: number
  nights: number
  image?: string
  status: 'active' | 'archived' | 'expired' | 'need-details' | 'unpublished'
  visa_required: boolean
  visa_info: string
  overview: string
  packageHighlights: string[]
  inclusions: string[]
  exclusions: string[]
  itinerary: ItineraryDay[]
  created_at: string
  updated_at: string
  expires_at?: string
  created_by?: number
  updated_by?: number
  created_by_name?: string
  created_by_email?: string
  updated_by_name?: string
  updated_by_email?: string
}

// (User types removed; no user management in v2)

// Activity can be either a string (legacy) or hierarchical object (new format)
export interface HierarchicalActivity {
  id: string
  activity: string
  subActivities: string[]
}

export type ActivityFormat = string | HierarchicalActivity

export interface ItineraryDay {
  day: number
  title: string
  activities: ActivityFormat[]
  meals: string[]
  accommodation?: string
}

export interface PackageFilters {
  status?: string
  category?: string
  search?: string
  _t?: number // Cache-busting timestamp
}

export interface CreatePackageData {
  title: string
  location: string
  category: string
  price: number
  duration: string
  image?: string
  status?: string
  overview?: string
  visa_required?: boolean
  visa_info?: string
  highlights?: string[]
  inclusions?: string[]
  exclusions?: string[]
  itinerary?: ItineraryDay[]
  expires_at?: string | null
}

// (Auth API removed)

// Package API methods
export const packagesApi = {
  // Get all packages with optional filters
  getAll: async (filters?: PackageFilters): Promise<ApiResponse<PackageData[]>> => {
    const queryParams = new URLSearchParams()
    if (filters?.status) queryParams.append('status', filters.status)
    if (filters?.category) queryParams.append('category', filters.category)
    if (filters?.search) queryParams.append('search', filters.search)
    // Always add cache-busting timestamp
    queryParams.append('_t', String(Date.now()))
    
    const queryString = queryParams.toString()
    const endpoint = `v2/packages.php${queryString ? `?${queryString}` : ''}`
    
    return apiFetch<PackageData[]>(endpoint)
  },

  // Get single package by ID
  getById: async (id: number): Promise<ApiResponse<PackageData>> => {
    return apiFetch<PackageData>(`v2/packages.php/${id}`)
  },

  // Create new package
  create: async (data: CreatePackageData): Promise<ApiResponse<{ id: number }>> => {
    return apiFetch<{ id: number }>('v2/packages.php', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update package
  update: async (id: number, data: Partial<PackageData>): Promise<ApiResponse<void>> => {
    // Defensive: include id in body and query to support server fallbacks
    const payload = { id, ...data }
    const endpoint = `v2/packages.php/${id}?id=${id}`
    return apiFetch<void>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  // Delete package
  delete: async (id: number): Promise<ApiResponse<void>> => {
    return apiFetch<void>(`v2/packages.php/${id}`, {
      method: 'DELETE',
    })
  },

  // Archive package (move to archived table)
  archive: async (id: number): Promise<ApiResponse<void>> => {
    return apiFetch<void>(`v2/packages.php/archive/${id}`, {
      method: 'PATCH',
    })
  },

  // Unpublish package (change status from active to unpublished)
  unpublish: async (id: number): Promise<ApiResponse<{ id: number; previous_status: string; new_status: string }>> => {
    return apiFetch<{ id: number; previous_status: string; new_status: string }>(`v2/packages.php/unpublish/${id}`, {
      method: 'PATCH',
    })
  },

  // Publish package (change status from unpublished back to active)
  publish: async (id: number): Promise<ApiResponse<{ id: number; previous_status: string; new_status: string }>> => {
    return apiFetch<{ id: number; previous_status: string; new_status: string }>(`v2/packages.php/publish/${id}`, {
      method: 'PATCH',
    })
  },

  // Restore archived package to active status
  restore: async (id: number): Promise<ApiResponse<void>> => {
    return apiFetch<void>(`v2/packages.php/restore/${id}`, {
      method: 'PATCH',
    })
  },

  // Get deleted packages
  getDeleted: async (filters?: Omit<PackageFilters, 'status'>): Promise<ApiResponse<PackageData[]>> => {
    const queryParams = new URLSearchParams()
    queryParams.append('deleted', 'true')
    if (filters?.category) queryParams.append('category', filters.category)
    if (filters?.search) queryParams.append('search', filters.search)
    if (filters?._t) queryParams.append('_t', String(filters._t))
    
    const queryString = queryParams.toString()
    const endpoint = `v2/packages.php?${queryString}`
    
    return apiFetch<PackageData[]>(endpoint)
  },

  // Get archived packages
  getArchived: async (filters?: Omit<PackageFilters, 'status'>): Promise<ApiResponse<PackageData[]>> => {
    const queryParams = new URLSearchParams()
    queryParams.append('archived', 'true')
    if (filters?.category) queryParams.append('category', filters.category)
    if (filters?.search) queryParams.append('search', filters.search)
    if (filters?._t) queryParams.append('_t', String(filters._t))

    const queryString = queryParams.toString()
    const endpoint = `v2/packages.php?${queryString}`

    return apiFetch<PackageData[]>(endpoint)
  },

  // Get expired packages
  getExpired: async (filters?: Omit<PackageFilters, 'status'>): Promise<ApiResponse<PackageData[]>> => {
    const queryParams = new URLSearchParams()
    queryParams.append('status', 'expired')
    if (filters?.category) queryParams.append('category', filters.category)
    if (filters?.search) queryParams.append('search', filters.search)
    if (filters?._t) queryParams.append('_t', String(filters._t))

    const queryString = queryParams.toString()
    const endpoint = `v2/packages.php?${queryString}`

    return apiFetch<PackageData[]>(endpoint)
  },
}

// Export API service
export const api = {
  packages: packagesApi,
  testimonials: {
    // Homepage testimonials: use v2 endpoint that returns minimal columns
    getHome: async (): Promise<ApiResponse<Array<{ id: number; rating: number; comment: string; created_at: string }>>> => {
      return apiFetch('v2/testimonials.php?limit=12')
    },
    
    // Admin testimonials CRUD operations  
    getAll: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<Array<{ id: number; rating: number; comment: string; created_at: string }>> & { total?: number; limit?: number; offset?: number }> => {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())
      const queryString = queryParams.toString()
      return apiFetch(`v2/testimonials.php${queryString ? '?' + queryString : ''}`)
    },
    
    get: async (id: number): Promise<ApiResponse<{ id: number; rating: number; comment: string; created_at: string }>> => {
      return apiFetch(`v2/testimonials.php/${id}`)
    },
    
    create: async (data: { rating: number; comment: string }): Promise<ApiResponse<{ id: string }>> => {
      return apiFetch('v2/testimonials.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
    },
    
    update: async (id: number, data: { rating?: number; comment?: string }): Promise<ApiResponse<{ message: string }>> => {
      return apiFetch(`v2/testimonials.php/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
    },
    
    delete: async (id: number): Promise<ApiResponse<Record<string, never>>> => {
      return apiFetch(`v2/testimonials.php/${id}`, {
        method: 'DELETE'
      })
    },
  },
}

export default api
